import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Tag, Input, Select, Button, Space, Card, Statistic, Row, Col, message
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, EyeOutlined, ShoppingOutlined,
  DollarOutlined, ClockCircleOutlined, CheckCircleOutlined, DownOutlined, CloseOutlined
} from '@ant-design/icons';
import { apiClient } from 'Utils/api';
import { useIsMobile } from 'Utils/useIsMobile';
import MobileListLayout from '../Components/MobileList/MobileListLayout';
import MobileCard from '../Components/MobileList/MobileCard';
import OptionSheet from '../Components/MobileList/OptionSheet';
import { Chip } from '../Components/ProductMobileList/ProductMobileList.style';

const { Search } = Input;
const { Option } = Select;

interface Order {
  id: string;
  orderNumber: string;
  customerEmail: string;
  customerFirstName: string;
  customerLastName: string;
  totalCents: number;
  totalEur: string;
  status: string;
  paymentStatus: string;
  itemCount: number;
  createdAt: string;
}

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ status: 'ALL', search: '' });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    revenue: 0,
  });

  const isMobile = useIsMobile();
  const [statusOpen, setStatusOpen] = useState(false);

  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [isMobile]);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line
  }, [pagination.current, filters, isMobile]);

  // Mobile appends pages, so a reload has to start over at page 1
  const reloadOrders = () => (isMobile && pagination.current > 1 ? setPagination(prev => ({ ...prev, current: 1 })) : fetchOrders());

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.current.toString(),
        take: pagination.pageSize.toString(),
        ...(filters.status !== 'ALL' && { status: filters.status }),
        ...(filters.search && { search: filters.search }),
      });

      const response = await apiClient.get(`/api/admin/orders?${params}`);
      const { orders: fetchedOrders, meta } = response.data;

      setOrders(prev => (isMobile && pagination.current > 1 ? [...prev, ...fetchedOrders] : fetchedOrders));
      setPagination(prev => ({ ...prev, total: Number(meta.total) || 0 }));

      // Calculate stats
      calculateStats(fetchedOrders);
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      if (error.response?.status !== 404) {
        message.error(error.response?.data?.message || 'Fehler beim Laden der Bestellungen');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (ordersData: Order[]) => {
    setStats({
      total: ordersData.length,
      pending: ordersData.filter(o => o.status === 'PENDING_PAYMENT' || o.status === 'PAID').length,
      completed: ordersData.filter(o => o.status === 'COMPLETED').length,
      revenue: ordersData.reduce((sum, o) => sum + (o.totalCents / 100), 0),
    });
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      PENDING_PAYMENT: 'orange',
      PAID: 'blue',
      SHIPPED: 'cyan',
      COMPLETED: 'green',
      CANCELLED: 'red',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      PENDING_PAYMENT: 'Zahlung ausstehend',
      PAID: 'Bezahlt',
      SHIPPED: 'Versendet',
      COMPLETED: 'Abgeschlossen',
      CANCELLED: 'Storniert',
    };
    return texts[status] || status;
  };

  const getPaymentStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      PENDING: 'orange',
      PAID: 'green',
      FAILED: 'red',
    };
    return colors[status] || 'default';
  };

  const markPaid = async (record: Order) => {
    try {
      await apiClient.patch(`/api/admin/orders/${record.id}`, {
        paymentStatus: 'PAID',
        status: record.status === 'PENDING_PAYMENT' ? 'PAID' : record.status,
      });
      message.success('Als bezahlt markiert');
      reloadOrders();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Fehler');
    }
  };

  const getPaymentStatusText = (status: string) => ({ PENDING: 'Ausstehend', PAID: 'Bezahlt', FAILED: 'Fehlgeschlagen' } as Record<string, string>)[status] || status;

  const columns = [
    {
      title: 'Bestellnr.',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      fixed: 'left' as const,
      width: 150,
    },
    {
      title: 'Kunde',
      key: 'customer',
      render: (_: any, record: Order) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.customerFirstName} {record.customerLastName}
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.customerEmail}</div>
        </div>
      ),
      width: 250,
    },
    {
      title: 'Artikel',
      dataIndex: 'itemCount',
      key: 'itemCount',
      width: 80,
      align: 'center' as const,
    },
    {
      title: 'Gesamt',
      dataIndex: 'totalCents',
      key: 'totalCents',
      width: 120,
      render: (cents: number) => (
        <span style={{ fontWeight: 600 }}>{(cents / 100).toLocaleString('de-DE')} €</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: 'Zahlung',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      width: 120,
      render: (status: string) => (
        <Tag color={getPaymentStatusColor(status)}>
          {status === 'PENDING' && 'Ausstehend'}
          {status === 'PAID' && 'Bezahlt'}
          {status === 'FAILED' && 'Fehlgeschlagen'}
        </Tag>
      ),
    },
    {
      title: 'Erstellt',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString('de-DE'),
    },
    {
      title: 'Aktionen',
      key: 'actions',
      fixed: 'right' as const,
      width: 240,
      render: (_: any, record: Order) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/orders/${record.id}`)}
          >
            Details
          </Button>
          {record.paymentStatus !== 'PAID' && (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              data-testid={`mark-paid-${record.orderNumber}`}
              onClick={(e) => {
                e.stopPropagation();
                markPaid(record);
              }}
            >
              Bezahlt
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleTableChange = (newPagination: any) => {
    setPagination(prev => ({ ...prev, current: newPagination.current }));
  };

  const setFilter = (key: 'status' | 'search', value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  if (isMobile) {
    const filtered = filters.status !== 'ALL' || !!filters.search;
    return (
      <>
        <MobileListLayout
          title="Bestellungen"
          action={<Button icon={<ReloadOutlined />} onClick={reloadOrders} aria-label="Aktualisieren" />}
          search={filters.search}
          onSearch={(value) => setFilter('search', value)}
          searchPlaceholder="Bestellnr., Kunde oder E-Mail …"
          filters={filters.status !== 'ALL' ? (
            <Chip type="button" onClick={() => setFilter('status', 'ALL')} data-testid="mobile-chip-status">
              <span>{getStatusText(filters.status)}</span>
              <CloseOutlined />
            </Chip>
          ) : (
            <Button onClick={() => setStatusOpen(true)} data-testid="mobile-status-open">Status <DownOutlined /></Button>
          )}
          count={`${pagination.total} ${pagination.total === 1 ? 'Bestellung' : 'Bestellungen'}`}
          loading={loading}
          isEmpty={orders.length === 0}
          emptyTitle="Keine Bestellungen gefunden"
          emptyText={filtered ? 'Keine Bestellung entspricht den aktuellen Filtern.' : 'Sobald im Shop bestellt wird, erscheinen die Bestellungen hier.'}
          onReset={filtered ? () => { setFilters({ status: 'ALL', search: '' }); setPagination(prev => ({ ...prev, current: 1 })); } : undefined}
          hasMore={orders.length < pagination.total}
          onLoadMore={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
        >
          {orders.map((order) => (
            <MobileCard
              key={order.id}
              testId={`mobile-order-card-${order.id}`}
              overline={`${order.orderNumber} · ${new Date(order.createdAt).toLocaleDateString('de-DE')}`}
              title={`${order.customerFirstName} ${order.customerLastName}`}
              highlight={(
                <>
                  {(order.totalCents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                  <Tag color={getStatusColor(order.status)}>{getStatusText(order.status)}</Tag>
                  <Tag color={getPaymentStatusColor(order.paymentStatus)}>{getPaymentStatusText(order.paymentStatus)}</Tag>
                </>
              )}
              meta={`${order.customerEmail} · ${order.itemCount} Artikel`}
              onOpen={() => navigate(`/orders/${order.id}`)}
              actions={[
                { key: 'details', label: 'Details', icon: <EyeOutlined />, onClick: () => navigate(`/orders/${order.id}`) },
                ...(order.paymentStatus !== 'PAID'
                  ? [{ key: 'paid', label: 'Als bezahlt markieren', icon: <CheckCircleOutlined />, onClick: () => markPaid(order) }]
                  : []),
              ]}
            />
          ))}
        </MobileListLayout>
        <OptionSheet
          open={statusOpen}
          title="Status"
          value={filters.status}
          options={['ALL', 'PENDING_PAYMENT', 'PAID', 'SHIPPED', 'COMPLETED', 'CANCELLED'].map((value) => ({ value, label: value === 'ALL' ? 'Alle Status' : getStatusText(value) }))}
          onClose={() => setStatusOpen(false)}
          onChange={(value) => { setFilter('status', value); setStatusOpen(false); }}
        />
      </>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ marginBottom: 24 }}>Bestellungen</h1>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Gesamt Bestellungen"
              value={stats.total}
              prefix={<ShoppingOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="In Bearbeitung"
              value={stats.pending}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Abgeschlossen"
              value={stats.completed}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Gesamtumsatz"
              value={stats.revenue}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="€"
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Search
              placeholder="Suche nach Bestellnr., Kunde oder E-Mail"
              allowClear
              enterButton={<SearchOutlined />}
              style={{ width: 400, maxWidth: '100%' }}
              onSearch={(value) => {
                setFilters(prev => ({ ...prev, search: value }));
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
            />
            <Select
              value={filters.status}
              style={{ width: 200 }}
              onChange={(value) => {
                setFilters(prev => ({ ...prev, status: value }));
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
            >
              <Option value="ALL">Alle Status</Option>
              <Option value="PENDING_PAYMENT">Zahlung ausstehend</Option>
              <Option value="PAID">Bezahlt</Option>
              <Option value="SHIPPED">Versendet</Option>
              <Option value="COMPLETED">Abgeschlossen</Option>
              <Option value="CANCELLED">Storniert</Option>
            </Select>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={fetchOrders}>
            Aktualisieren
          </Button>
        </Space>
      </Card>

      {/* Orders Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: false,
            showTotal: (total) => `Gesamt ${total} Bestellungen`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default OrdersPage;
